document.documentElement.classList.remove('no-js');

document.addEventListener('DOMContentLoaded', () => {
  const toggle = document.querySelector('[data-menu-toggle]');
  const nav = document.querySelector('[data-mobile-nav]');
  const backdrop = document.querySelector('[data-mobile-backdrop]');
  const closeButton = nav?.querySelector('[data-menu-close]');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let lastFocusedElement = null;
  let closeTimer = null;

  const menuFocusable = () => Array.from(nav?.querySelectorAll(
    'a[href], button:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'
  ) || []).filter((element) => !element.hasAttribute('hidden'));

  const finishMenuClose = () => {
    if (!nav || !backdrop) return;
    nav.hidden = true;
    backdrop.hidden = true;
  };

  const closeMenu = ({ returnFocus = true } = {}) => {
    if (!toggle || !nav || !backdrop) return;
    window.clearTimeout(closeTimer);
    nav.classList.remove('is-open');
    backdrop.classList.remove('is-open');
    document.body.classList.remove('menu-open');
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-label', 'Open menu');
    if (reducedMotion.matches) finishMenuClose();
    else closeTimer = window.setTimeout(finishMenuClose, 220);
    if (returnFocus && lastFocusedElement instanceof HTMLElement) lastFocusedElement.focus();
  };

  const openMenu = () => {
    if (!toggle || !nav || !backdrop) return;
    window.clearTimeout(closeTimer);
    lastFocusedElement = document.activeElement;
    nav.hidden = false;
    backdrop.hidden = false;
    window.requestAnimationFrame(() => {
      nav.classList.add('is-open');
      backdrop.classList.add('is-open');
    });
    document.body.classList.add('menu-open');
    toggle.setAttribute('aria-expanded', 'true');
    toggle.setAttribute('aria-label', 'Close menu');
    window.setTimeout(() => (closeButton || menuFocusable()[0])?.focus(), reducedMotion.matches ? 0 : 80);
  };

  if (toggle && nav && backdrop) {
    nav.hidden = true;
    backdrop.hidden = true;
    toggle.addEventListener('click', () => nav.classList.contains('is-open') ? closeMenu() : openMenu());
    closeButton?.addEventListener('click', () => closeMenu());
    backdrop.addEventListener('click', () => closeMenu());
    nav.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => closeMenu({ returnFocus: false })));

    document.addEventListener('keydown', (event) => {
      if (!nav.classList.contains('is-open')) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        closeMenu();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = menuFocusable();
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    window.addEventListener('resize', () => {
      if (window.innerWidth > 990 && nav.classList.contains('is-open')) closeMenu({ returnFocus: false });
    });
  }

  document.querySelectorAll('.product-form').forEach((form) => {
    const variantSelect = form.querySelector('[data-variant-select]');
    const optionSelects = Array.from(form.querySelectorAll('[data-product-option]'));
    const button = form.querySelector('[data-add-button]');
    const status = form.querySelector('[data-option-status]');
    const price = form.closest('.product-details')?.querySelector('[data-product-price]');
    if (!variantSelect) return;

    const variants = Array.from(variantSelect.options).map((option) => {
      const encoded = String(option.dataset.options || '');
      const values = encoded
        ? encoded.split('|||ENDOPTION|||')
        : [option.dataset.option1, option.dataset.option2, option.dataset.option3]
            .filter((value) => value !== undefined && value !== '');

      return {
        id: option.value,
        available: option.dataset.available === 'true',
        price: option.dataset.price || '',
        mediaId: option.dataset.mediaId || '',
        url: option.dataset.variantUrl || '',
        index: Number(option.dataset.variantIndex || 0),
        options: values.map((value) => String(value))
      };
    });

    const currentValues = () => optionSelects.map((select) => String(select.value));

    const exactVariant = (values) =>
      variants.find((variant) =>
        values.every((value, index) => variant.options[index] === value)
      );

    const closestVariant = (changedIndex, changedValue, values) => {
      const sameChangedValue = variants.filter((variant) =>
        variant.options[changedIndex] === changedValue
      );
      const available = sameChangedValue.filter((variant) => variant.available);
      const pool = available.length ? available : sameChangedValue;
      if (!pool.length) return null;

      return pool
        .map((variant) => ({
          variant,
          score: variant.options.reduce((total, value, index) => {
            if (index === changedIndex) return total + 20;
            return total + (value === values[index] ? 2 : 0);
          }, 0)
        }))
        .sort((a, b) => b.score - a.score)[0].variant;
    };

    const renderVariant = (variant, syncSelectors = true) => {
      if (!variant) return;

      variantSelect.value = variant.id;

      if (syncSelectors) {
        optionSelects.forEach((select, index) => {
          const value = variant.options[index];
          if (
            value !== undefined &&
            Array.from(select.options).some((option) => option.value === value)
          ) {
            select.value = value;
          }
        });
      }

      if (price && variant.price) price.textContent = variant.price;

      if (button) {
        button.disabled = !variant.available;
        button.textContent = variant.available
          ? button.dataset.availableText
          : button.dataset.soldText;
      }

      if (status) {
        status.textContent = variant.available
          ? 'Selected colour and size are available.'
          : 'This combination is currently sold out. Please choose another option.';
      }

      variantSelect.dispatchEvent(new CustomEvent('variant:changed', {
        bubbles: true,
        detail: variant
      }));
    };

    const optionChanged = (select) => {
      const changedIndex = Number(select.dataset.optionIndex || 0);
      const values = currentValues();
      let variant = exactVariant(values);

      if (!variant || !variant.available) {
        variant = closestVariant(changedIndex, select.value, values);
      }

      renderVariant(variant, true);
    };

    optionSelects.forEach((select) => {
      select.disabled = false;
      Array.from(select.options).forEach((option) => {
        option.disabled = false;
      });
      select.addEventListener('change', () => optionChanged(select));
    });

    const initial =
      variants.find((variant) => variant.id === variantSelect.value) ||
      exactVariant(currentValues()) ||
      variants.find((variant) => variant.available) ||
      variants[0];

    renderVariant(initial, true);
  });

  document.querySelectorAll('[data-collection-carousel]').forEach((carousel) => {
    const track = carousel.querySelector('[data-lines-track]');
    const cards = Array.from(carousel.querySelectorAll('[data-line-card]'));
    const dots = Array.from(carousel.querySelectorAll('[data-lines-dot]'));
    const previous = carousel.querySelector('[data-lines-prev]');
    const next = carousel.querySelector('[data-lines-next]');
    const status = carousel.querySelector('[data-lines-status]');
    if (!track || !cards.length) return;

    let activeIndex = 0;
    let timer = null;
    let scrollFrame = null;
    const wrap = (index) => (index + cards.length) % cards.length;
    const canScroll = () => track.scrollWidth > track.clientWidth + 8;

    const targetForCard = (card) => Math.max(0, Math.min(
      track.scrollWidth - track.clientWidth,
      card.offsetLeft - (track.clientWidth - card.offsetWidth) / 2
    ));

    const setActive = (index, { scroll = true, announce = true } = {}) => {
      activeIndex = wrap(index);
      cards.forEach((card, cardIndex) => {
        const active = cardIndex === activeIndex;
        card.classList.toggle('is-active', active);
        card.setAttribute('aria-current', active ? 'true' : 'false');
      });
      dots.forEach((dot, dotIndex) => {
        const active = dotIndex === activeIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', String(active));
      });
      if (status && announce) {
        const title = cards[activeIndex].querySelector('h3')?.textContent?.trim() || `Collection ${activeIndex + 1}`;
        status.textContent = `${title}, ${activeIndex + 1} of ${cards.length}`;
      }
      if (scroll && canScroll()) {
        track.scrollTo({
          left: targetForCard(cards[activeIndex]),
          behavior: reducedMotion.matches ? 'auto' : 'smooth'
        });
      }
    };

    const nearestIndex = () => {
      const centre = track.scrollLeft + track.clientWidth / 2;
      let nearest = 0;
      let distance = Number.POSITIVE_INFINITY;
      cards.forEach((card, index) => {
        const cardCentre = card.offsetLeft + card.offsetWidth / 2;
        const nextDistance = Math.abs(centre - cardCentre);
        if (nextDistance < distance) {
          nearest = index;
          distance = nextDistance;
        }
      });
      return nearest;
    };

    previous?.addEventListener('click', () => setActive(activeIndex - 1));
    next?.addEventListener('click', () => setActive(activeIndex + 1));
    dots.forEach((dot, index) => dot.addEventListener('click', () => setActive(index)));
    cards.forEach((card, index) => card.addEventListener('focusin', () => setActive(index, { scroll: false })));

    track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        setActive(activeIndex - 1);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        setActive(activeIndex + 1);
      }
    });

    track.addEventListener('scroll', () => {
      if (!canScroll() || scrollFrame) return;
      scrollFrame = window.requestAnimationFrame(() => {
        scrollFrame = null;
        const index = nearestIndex();
        if (index !== activeIndex) setActive(index, { scroll: false });
      });
    }, { passive: true });

    const autoEnabled = carousel.dataset.autoplay === 'true' && !reducedMotion.matches;
    const delay = Number(carousel.dataset.autoplaySpeed) || 6000;
    const stop = () => {
      window.clearInterval(timer);
      timer = null;
    };
    const start = () => {
      stop();
      if (autoEnabled && document.visibilityState === 'visible') {
        timer = window.setInterval(() => setActive(activeIndex + 1), delay);
      }
    };
    carousel.addEventListener('mouseenter', stop);
    carousel.addEventListener('mouseleave', start);
    carousel.addEventListener('focusin', stop);
    carousel.addEventListener('focusout', start);
    document.addEventListener('visibilitychange', () => document.visibilityState === 'visible' ? start() : stop());
    window.addEventListener('resize', () => setActive(activeIndex, { announce: false }));

    setActive(0, { scroll: false, announce: false });
    start();
  });

  document.querySelectorAll('[data-product-carousel]').forEach((carousel) => {
    const track = carousel.querySelector('[data-carousel-track]');
    const previous = carousel.closest('.ss-shop-panel')?.querySelector('[data-carousel-prev]');
    const next = carousel.closest('.ss-shop-panel')?.querySelector('[data-carousel-next]');
    const progress = carousel.querySelector('[data-carousel-progress]');
    const status = carousel.querySelector('[data-carousel-status]');
    const cards = Array.from(track?.querySelectorAll('.ss-product-card') || []);
    if (!track || cards.length < 2) {
      if (previous) previous.hidden = true;
      if (next) next.hidden = true;
      return;
    }

    const gap = () => parseFloat(getComputedStyle(track).gap || '0');
    const step = () => (cards[0]?.getBoundingClientRect().width || track.clientWidth) + gap();
    const maxScroll = () => Math.max(0, track.scrollWidth - track.clientWidth);
    const currentIndex = () => Math.min(cards.length - 1, Math.max(0, Math.round(track.scrollLeft / Math.max(step(), 1))));

    const update = () => {
      const max = maxScroll();
      const ratio = max > 0 ? Math.min(1, Math.max(0, track.scrollLeft / max)) : 1;
      if (progress) {
        const visibleRatio = Math.min(1, track.clientWidth / track.scrollWidth);
        progress.style.width = `${Math.max(12, visibleRatio * 100)}%`;
        progress.style.transform = `translateX(${ratio * (100 / Math.max(visibleRatio, .01) - 100)}%)`;
      }
      if (previous) previous.disabled = track.scrollLeft <= 4;
      if (next) next.disabled = track.scrollLeft >= max - 4;
      if (status) status.textContent = `Product ${currentIndex() + 1} of ${cards.length}`;
    };

    const move = (direction) => {
      const max = maxScroll();
      let target = track.scrollLeft + direction * step();
      if (direction > 0 && track.scrollLeft >= max - 4) target = 0;
      if (direction < 0 && track.scrollLeft <= 4) target = max;
      track.scrollTo({ left: target, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    };

    previous?.addEventListener('click', () => move(-1));
    next?.addEventListener('click', () => move(1));
    track.addEventListener('scroll', () => window.requestAnimationFrame(update), { passive: true });
    window.addEventListener('resize', update);

    let timer;
    const autoEnabled = carousel.dataset.autoplay === 'true' && !reducedMotion.matches;
    const delay = Number(carousel.dataset.autoplaySpeed) || 5000;
    const stop = () => window.clearInterval(timer);
    const start = () => {
      stop();
      if (autoEnabled && document.visibilityState === 'visible') timer = window.setInterval(() => move(1), delay);
    };
    carousel.addEventListener('mouseenter', stop);
    carousel.addEventListener('mouseleave', start);
    carousel.addEventListener('focusin', stop);
    carousel.addEventListener('focusout', start);
    document.addEventListener('visibilitychange', () => document.visibilityState === 'visible' ? start() : stop());
    update();
    start();
  });

  document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
    const items = Array.from(gallery.querySelectorAll('[data-product-media]'));
    const thumbs = Array.from(gallery.querySelectorAll('[data-product-thumbnail]'));
    const thumbTrack = gallery.querySelector('[data-gallery-thumbs]');
    const scrollButtons = Array.from(gallery.querySelectorAll('[data-gallery-scroll]'));
    const productMain = gallery.closest('.product-main');
    const select = productMain?.querySelector('[data-variant-select]');
    if (!items.length) return;

    const colourPosition = Number(gallery.dataset.colourOptionPosition || 0);
    let colourValues = [];
    try { colourValues = JSON.parse(gallery.dataset.colourValues || '[]'); } catch (error) { colourValues = []; }
    const variantCount = Number(gallery.dataset.variantCount || 0);
    const optionCount = Number(gallery.dataset.optionCount || 0);
    const productHandle = gallery.dataset.productHandle || '';

    const normalize = (value) => String(value || '')
      .toLowerCase()
      .replace(/[_/]+/g, '-')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    const containsColourToken = (haystack, colour) => {
      const source = normalize(haystack);
      const target = normalize(colour);
      if (!source || !target) return false;
      if (source === target || source.includes(`-${target}-`) || source.startsWith(`${target}-`) || source.endsWith(`-${target}`)) return true;
      return source.includes(target);
    };

    const showMedia = (mediaId) => {
      const target = items.find((item) =>
        item.dataset.productMedia === String(mediaId) &&
        item.dataset.galleryVisible !== 'false'
      );
      if (!target) return;
      items.forEach((item) => {
        const active = item === target;
        item.hidden = !active;
        item.classList.toggle('is-active', active);
        if (!active) item.querySelectorAll('video').forEach((video) => video.pause?.());
      });
      thumbs.forEach((thumb) => {
        const active = thumb.dataset.productThumbnail === String(mediaId) && !thumb.hidden;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-current', active ? 'true' : 'false');
        if (active) thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      });
    };

    const indexesForVariant = (option) => {
      if (!option) return null;

      // 1. Use Shopify's real variant -> media relationship whenever it exists.
      // We populate this from both Liquid (featured_media/featured_image) and
      // Shopify's product JSON below, which is especially useful for imported
      // Printful products whose variant media association is inconsistent.
      const featuredId = option.dataset.mediaId;

      // 2. If the product has a colour option, match the selected colour against
      // image alt text and the original image filename. This fixes imported
      // Printful products where variant featured_media was not populated.
      if (colourPosition > 0) {
        const selectedColour = option.dataset[`option${colourPosition}`] || '';
        if (selectedColour) {
          const matched = items
            .map((item, index) => ({ item, index }))
            .filter(({ item }) => containsColourToken(item.dataset.mediaSearch, selectedColour))
            .map(({ index }) => index);
          if (matched.length) {
            if (featuredId) {
              const featuredIndex = items.findIndex((item) => item.dataset.productMedia === String(featuredId));
              if (featuredIndex >= 0 && !matched.includes(featuredIndex)) matched.unshift(featuredIndex);
            }
            return matched;
          }
        }
      }

      // 3. For products whose images are arranged in equal groups by colour,
      // show that colour's complete image group. This is only a fallback.
      if (colourPosition > 0 && colourValues.length > 1) {
        const selectedColour = String(option.dataset[`option${colourPosition}`] || '').trim().toLowerCase();
        const colourIndex = colourValues.findIndex((value) => String(value).trim().toLowerCase() === selectedColour);
        const groups = Math.floor(items.length / colourValues.length);
        if (colourIndex >= 0 && groups >= 1 && items.length % colourValues.length === 0) {
          const start = colourIndex * groups;
          return Array.from({ length: groups }, (_, offset) => start + offset).filter((index) => index < items.length);
        }
      }

      // 4. One-option imported products: first try the exact featured image;
      // otherwise preserve the existing grouped-image fallback.
      if (featuredId) {
        const featuredIndex = items.findIndex((item) => item.dataset.productMedia === String(featuredId));
        if (featuredIndex >= 0) return [featuredIndex];
      }
      if (optionCount === 1 && variantCount > 1 && items.length >= variantCount * 2) {
        const variantIndex = Number(option.dataset.variantIndex || 0);
        const groups = Math.floor(items.length / variantCount);
        return Array.from({ length: Math.min(groups, 3) }, (_, group) => variantIndex + group * variantCount)
          .filter((index) => index < items.length);
      }
      return null;
    };

    const setVisibleMedia = (indexes) => {
      items.forEach((item) => {
        const visible = indexes === null || indexes.includes(Number(item.dataset.mediaIndex || 0));
        item.dataset.galleryVisible = visible ? 'true' : 'false';
        if (!visible) {
          item.hidden = true;
          item.classList.remove('is-active');
        }
      });
      thumbs.forEach((thumb) => {
        const visible = indexes === null || indexes.includes(Number(thumb.dataset.mediaIndex || 0));
        thumb.hidden = !visible;
        thumb.style.display = visible ? '' : 'none';
        if (!visible) thumb.classList.remove('is-active');
      });
      if (thumbTrack) thumbTrack.scrollLeft = 0;
    };

    const syncGallery = () => {
      const option = select?.options[select.selectedIndex];

      // Always keep the complete product gallery available. Selecting a colour
      // or size should change the featured image, not remove the other product
      // photos. This guarantees every product page exposes all of its Shopify
      // media through the thumbnail gallery.
      setVisibleMedia(null);

      const featuredId = option?.dataset.mediaId;
      const featured = featuredId ? items.find((item) =>
        item.dataset.productMedia === String(featuredId)
      ) : null;

      // If the variant has no featured media, use the first image that matches
      // the selected colour as the active image, while still keeping every
      // other product photo available in the gallery.
      let active = featured;
      if (!active && option) {
        const indexes = indexesForVariant(option);
        if (indexes?.length) active = items[indexes[0]];
      }

      const first = items[0];
      if (active) showMedia(active.dataset.productMedia);
      else if (first) showMedia(first.dataset.productMedia);

      if (option?.dataset.variantUrl && window.history?.replaceState) {
        window.history.replaceState({}, '', option.dataset.variantUrl);
      }
    };

    // Shopify's product JSON contains the variant featured_image mapping even
    // when the theme's Liquid object does not expose a featured_media value.
    // Load it once and merge those IDs into the existing variant options. This
    // makes colour -> image matching reliable across imported products.
    const hydrateVariantMedia = async () => {
      if (!productHandle || !select) return;
      try {
        const response = await fetch(`/products/${encodeURIComponent(productHandle)}.js`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin'
        });
        if (!response.ok) return;
        const data = await response.json();
        const byId = new Map((data.variants || []).map((variant) => [String(variant.id), variant]));
        Array.from(select.options).forEach((option) => {
          if (option.dataset.mediaId) return;
          const remote = byId.get(String(option.value));
          const mediaId = remote?.featured_image?.id || remote?.featured_media?.id || '';
          if (mediaId) option.dataset.mediaId = String(mediaId);
        });
        syncGallery();
      } catch (error) {
        // The server-rendered mapping remains the fallback if the JSON request
        // is unavailable.
      }
    };

    thumbs.forEach((thumb) => thumb.addEventListener('click', () => {
      if (!thumb.hidden) showMedia(thumb.dataset.productThumbnail);
    }));

    scrollButtons.forEach((button) => button.addEventListener('click', () => {
      if (!thumbTrack) return;
      const amount = Math.max(180, thumbTrack.clientWidth * 0.72);
      thumbTrack.scrollBy({
        left: button.dataset.galleryScroll === 'prev' ? -amount : amount,
        behavior: 'smooth'
      });
    }));

    select?.addEventListener('variant:changed', syncGallery);
    select?.addEventListener('change', syncGallery);
    syncGallery();
    hydrateVariantMedia();
  });

});


/* Still Standing: mobile product purchase bar */
document.addEventListener('DOMContentLoaded', function(){
  var bar=document.querySelector('[data-mobile-buybar]');
  var trigger=document.querySelector('[data-mobile-add]');
  var add=document.querySelector('[data-add-button]');
  if(!bar || !trigger || !add) return;
  function sync(){
    trigger.disabled=add.disabled;
    trigger.textContent=add.disabled ? 'SOLD OUT' : 'ADD TO BASKET';
    var price=document.querySelector('[data-product-price]');
    var mobilePrice=bar.querySelector('[data-mobile-buy-price]');
    if(price && mobilePrice) mobilePrice.textContent=price.textContent;
  }
  trigger.addEventListener('click',function(){ if(!trigger.disabled) add.click(); });
  document.addEventListener('change',sync);
  sync();
  var observer=new MutationObserver(sync);
  observer.observe(add,{attributes:true,childList:true,subtree:true});
});
